"use client"

import { useStoreData } from "@/hooks/store/useStoreData"
import { TypeStoreDetail } from "@/lib/redux/features/store/type"
import { Col, Modal, Row } from "antd"
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react"

export type PopupsRef = {
  close: () => void,
  open: (page: TypeStoreDetail['popups'][0]['event']['page']) => void
}

const Popups = forwardRef<PopupsRef, any>((props, ref) => {
  const { store } = useStoreData()
  const [open, setOpen] = useState<boolean | null>(null)
  const [popup, setPopup] = useState<number | null>(null)
  const [page, setPage] = useState<TypeStoreDetail['popups'][0]['event']['page'] | null>(null)

  const activePopups = useMemo(() => {
    return (store?.popups || []).filter((popup: any) => {
      return popup.event.page === page && popup.status === 'ACTIVE'
    })
  }, [page, store.popups])

  const handleOpen = useCallback((p: TypeStoreDetail['popups'][0]['event']['page']) => {
    const _activePopups = (store?.popups || []).filter((popup) => {
      return popup.event.page === p && popup.status === 'ACTIVE'
    })
    if (_activePopups.length > 0) {
      setPopup(0)
      setPage(p)
      setOpen(true)
    }
  }, [store.popups])

  const handleClose = useCallback(() => {
    if (popup !== null && popup < activePopups.length - 1) {
      setPopup(popup + 1)
    } else {
      setOpen(false)
      setPage(null)
      setPopup(null)
    }
  }, [activePopups, popup])

  useEffect(() => {
    if (!popup && activePopups.length > 0) {
      setPopup(0)
    }
  }, [popup, activePopups])

  useEffect(() => {
    if (popup === -1) {
      setPopup(null)
    }
  }, [props.page])

  useImperativeHandle(ref, () => {
    return {
      open: handleOpen,
      close: handleClose,
    }
  }, [handleClose, handleOpen])

  const dataPopup = popup !== null && !!activePopups[popup] ? activePopups[popup] : null

  return (
    <Modal
      open={!!open && !!dataPopup}
      title={dataPopup?.shown_name ? dataPopup?.name : undefined}
      width={dataPopup?.width || 700}
      height={dataPopup?.height}
      styles={{
        body: {
          height: dataPopup?.height
        },
        container: {
          backgroundColor: dataPopup?.bg?.color,
          backgroundImage: `url("${dataPopup?.bg?.image}")`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        },
        title: {
          fontSize: `${(dataPopup?.title?.size || 24) / 10}rem`,
          color: dataPopup?.title?.color,
          fontFamily: dataPopup?.title?.font_name,
          textAlign: (dataPopup?.title?.align as any) || 'left',
          ...(dataPopup?.title?.decorator === 'bold' ? {
            fontWeight: 'bold'
          } : {}),
          ...(dataPopup?.title?.decorator === 'italic' ? {
            fontStyle: 'italic'
          } : {}),
          ...(dataPopup?.title?.decorator === 'underline' ? {
            textDecoration: 'underline'
          } : {}),
        }
      }}
      footer={null}
      onCancel={handleClose}
    >
      <Row justify='center' style={{ height: "100%" }} align='middle'>
        <Col dangerouslySetInnerHTML={{ __html: dataPopup?.content?.html || "" }} />
      </Row>
    </Modal>
  )
})

export default Popups